/**
 * The optional per-workshop render slot a `WorkshopsCalendar` caller can
 * supply instead of the default `WorkshopsCalendarChip`.
 *
 * A context, not a prop drilled through `WorkshopsCalendarBody` /
 * `WorkshopsCalendarGrid`, so those two stay unaware the slot exists — only
 * the day cell (and the List view, for its click rule) read it.
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { WorkshopChip, WorkshopClickHandler } from './workshopsCalendarTypes';

export interface RenderWorkshopSlotProps {
  workshop: WorkshopChip;
  view: 'Month' | 'Week';
  /** Dense Month days collapse to the compact row until hovered. */
  compact: boolean;
  onWorkshopClick?: WorkshopClickHandler;
}

export type RenderWorkshopSlot = (props: RenderWorkshopSlotProps) => ReactNode;

const WorkshopsCalendarRenderContext = createContext<RenderWorkshopSlot | undefined>(undefined);

export const WorkshopsCalendarRenderProvider = WorkshopsCalendarRenderContext.Provider;

/** The caller's slot, or `undefined` when the calendar renders the default
 *  chip for every workshop. */
export function useWorkshopRenderSlot(): RenderWorkshopSlot | undefined {
  return useContext(WorkshopsCalendarRenderContext);
}
